import { useCallback, useState } from 'react';
import { ApiError } from '@agnivo/api-client';
/**
 * IDENTITY_NOT_PROVISIONED — "valid Zitadel token, no platform `users` row".
 *
 * The symptom this exists for (ADR-089 FINDING-4, found live by the desktop
 * headless e2e harness): a user completes OIDC login, gets a valid session and a
 * fully rendered shell, and then every single call fails. Before the gateway fix
 * that was an indistinguishable 401 — byte-identical to an outage, and worse, it
 * invited the generic auth handler to re-authenticate, which SUCCEEDS (the token
 * really is valid), which 401s again: a login loop that can never resolve.
 *
 * Architect ruled it a distinguishable failure — the rule: a valid token with no
 * platform identity MUST NOT be reported as an auth failure, because that shape is
 * indistinguishable from an outage and re-triggers the loop above. Provenance:
 * `agents/shared/decisions/architect/2026-07-26-valid-token-no-platform-identity-must-be-a-distinguishable-failure.md`
 * (2026-07-26; fleet-internal, not in this repo).
 * The gateway now returns FailedPrecondition +
 * google.rpc.ErrorInfo(domain, reason) — landed on gateway `dev` in
 * internal/adapter/middleware/auth/ingressjwterror.go.
 *
 * Both transports surface it the SAME way, which is why one handler covers the
 * whole surface: the unary path via the gRPC interceptors, and the SSE reply
 * stream because its identity check fails CLOSED PRE-STREAM (verified in
 * gateway replystream.go — `refuse()` writes HTTP status + google.rpc.Status
 * JSON and the stream never opens). So this is a plain ApiError on every path,
 * NOT an in-stream error event — unlike ADR-092 AGENT_NOT_AVAILABLE, which can
 * only arrive in-stream because the gateway writes the SSE header first.
 */
export const IDENTITY_NOT_PROVISIONED_DOMAIN = 'gateway.agnivo.org';
export const IDENTITY_NOT_PROVISIONED_REASON = 'IDENTITY_NOT_PROVISIONED';
/**
 * Default bot chat — the DEV bot, deliberately.
 *
 * The bot is the only honest entry point for this state (product 2026-07-26), and
 * product's spec names `t.me/agnivo_ai_bot` — the PROD bot. Hardcoding that would
 * be wrong per-environment, not just untidy: provisioning happens on whichever
 * environment the bot belongs to, so a dev/staging user sent to the prod bot gets
 * an identity that does nothing for the surface they are actually stuck on. The
 * card would look like a remedy and be a second dead end.
 *
 * So the base URL is shell-injected (`AppConfig.botChatUrl`) and defaults to DEV —
 * the same default-to-dev rule the mini-app already applies to `voiceFallbackUrl`,
 * for the same reason: a surface that forgot to configure it must not silently
 * point at production. Prod builds set it explicitly.
 */
/**
 * Exported (not just identity-gap-local) because it is a fact about the fleet's
 * bot-chat default, not about this recovery flow specifically — the
 * agent-limit-hit upgrade CTA (`AgentCreateScreen.tsx`) reuses it for the same
 * reason: default-to-dev so a surface that forgot to configure `botChatUrl`
 * cannot silently point a user at production.
 */
export const DEFAULT_BOT_URL = 'https://t.me/agnivo_dev_ai_bot';
/**
 * Recovery deep link for the surface this card renders on.
 *
 * `?start=<surface>` is the attribution half of the measurement: the gateway's own
 * counter says how often this state FIRES, this says how many of those users then
 * open the bot. Neither is the conversion rate alone.
 */
export function identityGapBotUrl(surface, botChatUrl) {
    return `${botChatUrl ?? DEFAULT_BOT_URL}?start=${surface}`;
}
/**
 * Match the (domain, reason) TUPLE — never the gRPC code, never the message.
 * A reason is only unique within its domain, and distinct recovery signals
 * deliberately share codes (architect ruling 2026-07-15); the message is prose
 * and may be reworded at any time.
 */
export function isIdentityNotProvisioned(err) {
    if (!(err instanceof ApiError))
        return false;
    const info = err.errorInfo;
    return (info?.domain === IDENTITY_NOT_PROVISIONED_DOMAIN &&
        info?.reason === IDENTITY_NOT_PROVISIONED_REASON);
}
/**
 * Observe every live API error and latch the identity gap.
 *
 * STICKY on purpose: nothing the app can do — no retry, no re-auth — changes the
 * outcome, so un-latching on a later error would flip the user back into a shell
 * where everything is still broken.
 *
 * **This comment used to end "the state clears the way it was entered: a reload
 * after the bot has provisioned them." That was false, and it is worth saying so
 * rather than deleting it**, because the sentence outlived its own evidence and
 * then got quoted back as the copy's justification (frontend, 2026-08-10 — I
 * reported the CARD as promising a reload; it never did, this comment did).
 *
 * What developer established at source on 2026-08-10: opening the bot does NOT
 * make an existing Zitadel sub resolve. `users.zitadel_user_id` has exactly three
 * writers in tenant-service and all three are CREATION paths; `AutoProvisionInput`
 * has no Zitadel-user-id field; and on a reuse-hit `AutoProvision` unconditionally
 * re-calls `CreateUser`. So the bot mints a NEW platform principal keyed on the
 * Telegram identity, and the sub this browser session holds keeps no platform row
 * — a reload after opening the bot returns the identical gate, and a second
 * attempt mints a second orphan.
 *
 * So the honest statement of the lifecycle is: **this latch does not clear for the
 * session that set it.** It clears only for a session whose sub HAS a platform row
 * — i.e. a different login. That is why the copy is a redirect and not a
 * sequencing step (see the `identityGap` docstring in `i18n.ts`). Whether the
 * orphan population is real at all turns on Zitadel self-registration, which is
 * live-config, not code: SRE holds that query (architect, 2026-08-10). An adoption
 * path that WOULD make this clear in place is un-ruled, not rejected.
 *
 * Class scope, same as the mini-app's tg_uid-missing handler: the signal is
 * session-fatal wherever it appears, so ANY call carrying it gates the surface —
 * it is not per-request error state.
 */
export function useIdentityGap() {
    const [gapped, setGapped] = useState(false);
    const onApiError = useCallback((err) => {
        if (isIdentityNotProvisioned(err))
            setGapped(true);
    }, []);
    return { gapped, onApiError };
}
//# sourceMappingURL=identityGap.js.map